import "@mantine/core/styles.css";
import "@mantine/carousel/styles.css";
import "./global.css";

import { ConfigProvider } from "@app/app/providers/ConfigProvider.tsx";
import { FullscreenFallback } from "@app/app/routing/FullscreenFallback.tsx"; // <-- Новый компонент
import { FxGamesTheme } from "@app/config/Themes.tsx";
import { AcceptDocumentsModal } from "@app/features/accept-documents/ui/AcceptDocumentsModal.tsx";
import { SocialModal } from "@app/features/friends/ui/SocialModal.tsx";
import { ConfirmPurchaseWithPersonalInfoInputModal } from "@app/features/payment/ui/ConfirmPurchaseWithPersonalInfoInputModal.tsx";
import { LinkShareModal } from "@app/features/share-link/ui/LinkShareModal.tsx";
import { useUrlQuery as useUrlQuery } from "@app/shared/lib/Hooks/useQuery.tsx";
import { HelmetWithChildren } from "@app/shared/ui/HelmetWithChildren";
import { IframeModal } from "@app/shared/ui/Modal/IframeModal.tsx";
import { InfoModal } from "@app/shared/ui/Modal/InfoModal.tsx";
import { TextCopyModal } from "@app/shared/ui/Modal/TextCopyModal.tsx";
import { InstallGame, NoEmail, ValidateEmail } from "@app/widgets/GlobalMessages/Messages";
import { AuthLoginModalView } from "@features/authV2/ui/AuthLoginModalView.tsx";
import { Button, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { DocumentModal } from "@pages/LandingPage/DocumentViewerModal.tsx";
import { ErrorBoundary } from "@sentry/react";
import ProfileModal from "@shared/ui/Modal/ProfileModal.tsx";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { GlobalMessagesProvider } from "@widgets/GlobalMessages";
import { Suspense } from "react";
import { I18nextProvider } from "react-i18next";
import { IconContext } from "react-icons";

import { queryClient } from "../../packages/fxid-openapi-client/src/QueryClient.ts";
import { IconProvider } from "../assets/Icons";
import { StripeCheckoutForm } from "../features/payment/ui/StripeCheckoutModal.tsx";
import { ReportIssueModal } from "../features/report-issue/ui/ReportIssueModal.tsx";
import i18n from "./providers/i18n.tsx";
import AppRouter from "./routing/AppRouter.tsx";

/**
 * Корневой компонент приложения.
 * Отвечает за инициализацию всех глобальных провайдеров.
 */
const AppContent = () => {
	log.info("Render App (Providers Root)...");
	const urlQuery = useUrlQuery();

	return (
		<I18nextProvider i18n={i18n}>
			<IconProvider>
				<IconContext.Provider value={{ className: "react-icon" }}>
					<MantineProvider theme={FxGamesTheme} defaultColorScheme="dark">
						<DocumentModal></DocumentModal>
						{/* Скрипты для отладки */}
						{urlQuery.has("remotejs") && (
							<HelmetWithChildren>
								<script
									data-consolejs-channel={urlQuery.get("remotejs")}
									src="https://remotejs.com/agent/agent.js"
								></script>
							</HelmetWithChildren>
						)}
						{urlQuery.has("eruda") && (
							<HelmetWithChildren>
								<script src="//cdn.jsdelivr.net/npm/eruda"></script>
								<script>eruda.init();</script>
							</HelmetWithChildren>
						)}

						{/* Основной роутер приложения */}
						{/* ErrorBoundary перехватывает ошибки из useSuspenseQuery */}
						<ErrorBoundary fallback={<p>An error has occurred</p>}>
							{/* Suspense показывает заглушку, пока ConfigProvider загружает данные */}
							<Suspense fallback={<FullscreenFallback />}>
								<ConfigProvider>
									<ModalsProvider
										modals={{
											iframe: IframeModal,
											loginModal: AuthLoginModalView,
											confirmPurchaseWithPersonalInfoModal:
												ConfirmPurchaseWithPersonalInfoInputModal,
											reportIssue: ReportIssueModal,
											linkShare: LinkShareModal,
											textCopyModal: TextCopyModal,
											social: SocialModal,
											stripeCheckoutModal: StripeCheckoutForm,
											info: InfoModal,
											acceptDocuments: AcceptDocumentsModal,
											profileModal: ProfileModal,

										}}
										modalProps={{ centered: true, size: "xl" }}
									>
										<AppRouter />
									</ModalsProvider>
								</ConfigProvider>
							</Suspense>
						</ErrorBoundary>
					</MantineProvider>
				</IconContext.Provider>
			</IconProvider>
		</I18nextProvider>
	);
};

/**
 * Корневой компонент приложения.
 * Отвечает за инициализацию QueryClientProvider.
 */
const App = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<AppContent />
			{import.meta.env.DEV && <ReactQueryDevtools />}
		</QueryClientProvider>
	);
};

export default App;
